import { AxiosResponse } from 'axios'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import Header from '../components/header'
import { useAuth } from '../contexts/AuthContext'
import { ContactUsFormValues, useContactUsForm } from '../forms/contact-us-form'
import { Account } from '../models/account'
import { backend } from '../utils/axios'

declare global {
  interface Window {
    grecaptcha: any
    onloadCallback: () => void
    verifyCallback: (response: string) => void
  }
}

type ContactUsRequest = {
  name: string
  phone: string
  email: string
  message: string
}

const ROUTE = '/contact-us'

export default function ContactUs() {
  const { account } = useAuth()
  const { register, handleSubmit, formState } = useContactUsForm(
    account as Account
  )
  const widgetId = useRef<number | null>(null)

  const [token, setToken] = useState<string | null>(null)
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

  const verifyCallback = useCallback((response: any) => {
    if (response) {
      setToken(response)
    }
  }, [])

  useEffect(() => {
    window.verifyCallback = verifyCallback

    const script = document.createElement('script')
    script.src =
      'https://www.google.com/recaptcha/enterprise.js?onload=onloadCallback&render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)

    window.onloadCallback = () => {
      widgetId.current = window.grecaptcha.enterprise.render('g-recaptcha', {
        sitekey: process.env.REACT_APP_RECAPTCHA_KEY,
        callback: verifyCallback,
        theme: 'light',
      })
    }

    // Optional: Cleanup function to remove script if component unmounts
    return () => {
      document.head.removeChild(script)
    }
  }, [verifyCallback])

  const onSubmit = async (values: ContactUsFormValues) => {
    const validToken = window.grecaptcha.enterprise.getResponse(
      widgetId.current
    )

    if (!validToken) {
      return
    } else {
      const createData = {
        name: values.name,
        phone: values.phone,
        email: values.email,
        message: values.message,
      }

      const res = await backend
        .post<ContactUsRequest, AxiosResponse<boolean>>(`${ROUTE}`, createData)
        .then(({ data }) => data)
        .catch()
      if (res) {
        setSubmittedSuccessfully(true)
      }
      return res
    }
  }

  return (
    <>
      <Header>
        <div className="container w-full mx-auto">
          <div className="container h-[80px] mx-auto text-3xl text-white font-semibold flex items-center">
            Contact Us
          </div>
        </div>
      </Header>
      {submittedSuccessfully ?
        <div className='flex flex-col items-center justify-center w-full h-1/2 min-h-3/4-screen'>
          <div className="flex flex-col items-center justify-center flex-grow w-11/12 px-10 bg-blue-100 rounded-sm my-28">
            <div className="flex flex-col items-center justify-center mt-5 text-lg text-center">
              Thank you for contacting us. Your request has been received.
            </div>
          </div>
        </div>
        :
        <div className="container flex justify-between max-w-5xl mx-auto my-16">
          <form
            className="container flex flex-col max-w-2xl gap-8"
            onSubmit={handleSubmit(onSubmit)}
          >
            <p className="leading-7">
              <b>Have more questions?</b> Contact us today! We take pride in
              working with our clients in servicing their election needs.
            </p>
            <div>
              <label
                htmlFor="name"
                className="font-medium tracking-widest uppercase"
              >
                Name
              </label>
              <input
                {...register('name')}
                type="text"
                name="name"
                placeholder="Name"
                className={classNames(
                  'w-full mt-2 px-4 py-3 border rounded-md border-primary-blue',
                  {
                    'border-red-500 focus:outline-red-500':
                      formState?.errors?.name,
                  }
                )}
              />
              {formState?.errors?.name && (
                <div className="mt-1 text-sm text-red-500">
                  {formState.errors.name.message}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="phone"
                className="font-medium tracking-widest uppercase"
              >
                Phone number
              </label>
              <input
                {...register('phone')}
                type="text"
                name="phone"
                placeholder="Phone Number"
                className={classNames(
                  'w-full mt-2 px-4 py-3 border rounded-md border-primary-blue',
                  {
                    'border-red-500 focus:outline-red-500':
                      formState?.errors?.phone,
                  }
                )}
              />
              {formState?.errors?.phone && (
                <div className="mt-1 text-sm text-red-500">
                  {formState.errors.phone.message}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="email"
                className="font-medium tracking-widest uppercase"
              >
                Email
              </label>
              <input
                {...register('email')}
                type="text"
                name="email"
                placeholder="Email"
                className={classNames(
                  'w-full mt-2 px-4 py-3 border rounded-md border-primary-blue',
                  {
                    'border-red-500 focus:outline-red-500':
                      formState?.errors?.email,
                  }
                )}
              />
              {formState?.errors?.email && (
                <div className="mt-1 text-sm text-red-500">
                  {formState.errors.email.message}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="message"
                className="font-medium tracking-widest uppercase"
              >
                Your Message
              </label>
              <textarea
                {...register('message')}
                name="message"
                placeholder="Your Message"
                className={classNames(
                  'w-full mt-2 px-4 py-3 border rounded-md border-primary-blue min-h-[150px]',
                  {
                    'border-red-500 focus:outline-red-500':
                      formState?.errors?.message,
                  }
                )}
              />
              {formState?.errors?.message && (
                <div className="mt-1 text-sm text-red-500">
                  {formState.errors.message.message}
                </div>
              )}
            </div>
            <div id="g-recaptcha" className="g-recaptcha"></div>
            <div className="self-end">
              <button
                type="submit"
                disabled={
                  ((formState.isSubmitting || !formState.isValid) &&
                    formState.isSubmitted) ||
                  !token
                }
                className={classNames(
                  'uppercase inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-40 disabled:cursor-not-allowed'
                )}
              >
                Send Message
              </button>
            </div>
          </form>
          <div className="max-w-[280px] container">
            <div className="w-full bg-[#E6EBF5] flex flex-col gap-8 p-8 leading-7">
              <div>
                <p>
                  <b>ElectionSource Office</b>
                </p>
                <p>4615 Danvers Dr. SE</p>
                <p>Grand Rapids, MI 49512</p>
              </div>
              <div>
                <p>
                  <b>Phone</b>
                </p>
                <p>(888)-742-8037</p>
              </div>
            </div>
          </div>
        </div>}
    </>
  )
}
